import { useCallback, useEffect, useState } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Riquadro, Tabella } from '../../componenti/base.tsx'

/**
 * Integrità del registro eventi (SIC-63, SIC-65).
 *
 * Il registro dichiara di non essere modificabile. Questo riquadro è il posto in cui quella
 * dichiarazione si può controllare invece di crederci: le ultime verifiche con il loro esito,
 * i sigilli depositati fuori dal database, e il comando per rifare la verifica adesso.
 *
 * Sta in un componente suo, e non dentro la pagina del registro, perché è rivolto a un'altra
 * persona: l'elenco degli eventi lo legge chi indaga su un fatto, questo lo guarda chi risponde
 * dell'integrità del registro — ed è il permesso `audit.verifica` a separarli.
 */

type Verifica = {
  id: string
  eseguita_il: string
  tipo: string
  esito: 'ok' | 'anomalia'
  dettaglio: string | null
}

type Sigillo = {
  id: string
  classe: string
  id_da: number | null
  id_a: number | null
  eventi: number
  hash_radice: string
  firmato: boolean
  ancora_key: string | null
  creato_il: string
}

type EsitoVerifica = {
  esaminati: number
  ultimo_id: number
  altri_da_esaminare: boolean
  buchi: number
  sigilli_verificati: number
  non_sigillati: number | null
  controlli_non_eseguiti: string[]
  esito: 'ok' | 'anomalia'
  anomalie: { id: number; problema: string }[]
}

const quando = (iso: string) => new Date(iso).toLocaleString('it-IT')

/**
 * La verifica legge il registro a blocchi, e la pagina li percorre tutti.
 *
 * Chiamata una volta sola, `POST /audit/verifica` guarda i primi cinquemila eventi e risponde
 * «nessuna anomalia»: su un registro piu' lungo quella risposta copriva il solo inizio, e non
 * lo diceva. Qui si continua da `ultimo_id` finche' il registro finisce. I sigilli si
 * confrontano al primo blocco: coprono gia' tutto il registro, rifarlo a ogni blocco sarebbe
 * lo stesso lavoro ripetuto.
 */
const EVENTI_PER_BLOCCO = 50_000
const BLOCCHI_MASSIMI = 20

function unisci(a: EsitoVerifica, b: EsitoVerifica): EsitoVerifica {
  return {
    esaminati: a.esaminati + b.esaminati,
    ultimo_id: b.ultimo_id,
    altri_da_esaminare: b.altri_da_esaminare,
    buchi: a.buchi + b.buchi,
    sigilli_verificati: Math.max(a.sigilli_verificati, b.sigilli_verificati),
    non_sigillati: a.non_sigillati ?? b.non_sigillati,
    controlli_non_eseguiti: a.controlli_non_eseguiti,
    esito: a.esito === 'anomalia' || b.esito === 'anomalia' ? 'anomalia' : 'ok',
    anomalie: [...a.anomalie, ...b.anomalie].slice(0, 50),
  }
}

export function Integrita() {
  const { puo } = useSessione()
  const [verifiche, setVerifiche] = useState<Verifica[] | null>(null)
  const [sigilli, setSigilli] = useState<Sigillo[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [appena, setAppena] = useState<EsitoVerifica | null>(null)

  const carica = useCallback(() => {
    setErrore(null)
    Promise.all([
      api.get<Verifica[]>('/audit/verifiche'),
      api.get<Sigillo[]>('/audit/sigilli'),
    ]).then(([v, s]) => { setVerifiche(v); setSigilli(s) })
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'))
  }, [])
  useEffect(carica, [carica])

  // Il permesso lo verifica comunque l'API: qui si evita solo di mostrare un comando che
  // risponderebbe «non trovato».
  if (!puo('audit.verifica')) return null

  async function verificaOra() {
    setInCorso(true); setErrore(null); setAppena(null)
    try {
      let complessivo: EsitoVerifica | null = null
      let dalId = 0
      for (let blocco = 0; blocco < BLOCCHI_MASSIMI; blocco++) {
        const parziale = await api.post<EsitoVerifica>('/audit/verifica', {
          dal_id: dalId, massimo: EVENTI_PER_BLOCCO, sigilli: blocco === 0,
        })
        complessivo = complessivo ? unisci(complessivo, parziale) : parziale
        if (!parziale.altri_da_esaminare) break
        dalId = parziale.ultimo_id
      }
      setAppena(complessivo)
      carica()
    } catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
    finally { setInCorso(false) }
  }

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      <Riquadro
        titolo="Integrità del registro"
        azioni={
          <Bottone variante="primario" disabled={inCorso} onClick={() => void verificaOra()}>
            {inCorso ? 'Verifica in corso…' : 'Verifica adesso'}
          </Bottone>
        }
      >
        <p className="tenue">
          Il registro non si modifica: le scritture diverse dall’inserimento vengono rifiutate dal
          database. Ogni ora la catena degli eventi viene sigillata e l’impronta depositata fuori
          dal database, su un archivio che non consente la cancellazione. La verifica ricalcola le
          impronte e le confronta con quelle depositate.
        </p>
        {appena && (
          <Avviso tipo={appena.esito === 'ok' ? 'successo' : 'errore'}>
            {appena.esito === 'ok'
              ? `Nessuna anomalia: ${appena.esaminati} eventi e ${appena.sigilli_verificati} sigilli verificati`
                + (appena.non_sigillati ? `, ${appena.non_sigillati} eventi non ancora sigillati.` : '.')
              : `${appena.anomalie.length} anomalie su ${appena.esaminati} eventi verificati.`}
            {appena.buchi > 0 && ` ${appena.buchi} ordinali mancanti (normale: una transazione annullata ne consuma uno).`}
            {appena.altri_da_esaminare
              && ` Il registro continua oltre l’evento ${appena.ultimo_id}: la verifica si è fermata qui.`}
          </Avviso>
        )}
        {appena?.controlli_non_eseguiti.length ? (
          // Un «nessuna anomalia» ottenuto senza guardare l'ancora depositata e senza verificare
          // le firme vale molto meno, e chi legge deve saperlo.
          <Avviso tipo="attenzione">
            Confronti non eseguiti, perché non configurati:
            <ul>
              {appena.controlli_non_eseguiti.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </Avviso>
        ) : null}
        {appena?.anomalie.length ? (
          <ul>
            {appena.anomalie.map((a) => (
              <li key={`${a.id}-${a.problema}`}>Evento {a.id}: {a.problema}</li>
            ))}
          </ul>
        ) : null}
      </Riquadro>

      <Riquadro titolo="Ultime verifiche">
        {!verifiche ? <Attesa /> : verifiche.length === 0
          ? <Vuoto testo="Nessuna verifica registrata." />
          : (
            <Tabella intestazioni={['Quando', 'Tipo', 'Esito', 'Dettaglio']}>
              {verifiche.map((v) => (
                <tr key={v.id}>
                  <td>{quando(v.eseguita_il)}</td>
                  <td>{v.tipo}</td>
                  <td>
                    <Etichetta tono={v.esito === 'ok' ? 'positivo' : 'critico'}>{v.esito}</Etichetta>
                  </td>
                  <td className="piccolo">{v.dettaglio ?? '—'}</td>
                </tr>
              ))}
            </Tabella>
          )}
      </Riquadro>

      <Riquadro titolo="Sigilli depositati">
        {!sigilli ? <Attesa /> : sigilli.length === 0
          ? <Vuoto testo="Nessun sigillo depositato: il primo arriva al prossimo giro orario." />
          : (
            <Tabella intestazioni={['Quando', 'Eventi', 'Intervallo', 'Radice', 'Firma', 'Ancora']}>
              {sigilli.map((s) => (
                <tr key={s.id}>
                  <td>{quando(s.creato_il)}</td>
                  <td>{s.eventi}</td>
                  <td>{s.id_da != null ? `${s.id_da}–${s.id_a}` : '—'}</td>
                  {/* L'impronta intera è illeggibile e non serve a nessuno a schermo: le prime
                      cifre bastano a riconoscere il sigillo, e il valore per intero sta
                      nell'ancora depositata. */}
                  <td className="lavori-audit-impronta" title={s.hash_radice}>
                    {s.hash_radice.slice(0, 16)}…
                  </td>
                  <td>
                    <Etichetta tono={s.firmato ? 'positivo' : 'neutro'}>
                      {s.firmato ? 'firmato' : 'non firmato'}
                    </Etichetta>
                  </td>
                  <td className="piccolo">{s.ancora_key ?? '—'}</td>
                </tr>
              ))}
            </Tabella>
          )}
      </Riquadro>
    </>
  )
}
